import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import arrowIcon from '@/assets/icons/faq/material-symbols_arrow-back-rounded.svg';

import styles from './FAQQuestion.module.scss';

interface FAQQuestionProps {
  className?: string;
  question: string;
  answer: string;
}

export const FAQQuestion = ({ className, question, answer }: FAQQuestionProps) => {
  const containerPadding = 15;
  const containerGap = 15;

  const [isOpened, setIsOpened] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const questionRef = useRef<HTMLDivElement>(null);
  const answerRef = useRef<HTMLDivElement>(null);
  const [minHeight, setMinHeight] = useState<number | undefined>(undefined);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const recalcHeights = useCallback(() => {
    if (answerRef.current && questionRef.current) {
      const containerMinHeight = questionRef.current.offsetHeight + containerPadding * 2;
      setMinHeight(containerMinHeight);
      const containerMaxHeight =
        questionRef.current.offsetHeight +
        answerRef.current.scrollHeight +
        containerPadding * 2 +
        containerGap;
      setMaxHeight(containerMaxHeight);
      setIsReady(true);
    }
  }, [containerPadding, containerGap]);

  useEffect(() => {
    recalcHeights();
  }, [isOpened, recalcHeights]);

  useEffect(() => {
    window.addEventListener('resize', recalcHeights);
    return () => window.removeEventListener('resize', recalcHeights);
  }, [recalcHeights]);

  return (
    <div
      role="button"
      tabIndex={0}
      className={clsx(
        styles.faqQuestion,
        {
          [styles.faqQuestion_opened]: isOpened,
          [styles.faqQuestion_withTransition]: isReady,
        },
        className,
      )}
      style={{
        height: isOpened ? maxHeight : minHeight,
      }}
      onClick={() => setIsOpened(!isOpened)}
    >
      <div ref={questionRef} className={styles.faqQuestion__questionWrapper}>
        <p className={styles.faqQuestion__question}>{question}</p>
        <div className={styles.faqQuestion__iconWrapper}>
          <img className={styles.faqQuestion__icon} src={arrowIcon} alt="Arrow" />
        </div>
      </div>
      <p ref={answerRef} className={styles.faqQuestion__answer}>
        {answer}
      </p>
    </div>
  );
};
